import { useEffect, useRef, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import { apiClient } from "~/utils/apiClient";
import { PANE_MCP_SERVER_IDS } from "./useMcpAppsPane";

interface McpAppEntry {
  html: string | null;
  title: string;
  toolResult: unknown;
}

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

export type { McpAppEntry };

export function useMcpAppEvents(agent: HttpAgent): Map<string, McpAppEntry> {
  const [entries, setEntries] = useState<Map<string, McpAppEntry>>(() => new Map());
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setEntries(new Map());

    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        setEntries(new Map());
      },
      onCustomEvent: ({ event }: { event: unknown }) => {
        const e = event as { name?: string; value?: Record<string, unknown> };
        if (e.name !== "mcp_app_ui") return;

        const v = e.value;
        if (!v?.tool_call_id || !v.resource_uri || !v.mcp_server_id) return;

        const toolCallId = v.tool_call_id as string;
        const resourceUri = v.resource_uri as string;
        const mcpServerId = v.mcp_server_id as string;
        // Pane-hosted apps render in the side pane, not inline in the turn.
        if (PANE_MCP_SERVER_IDS.has(mcpServerId)) return;
        const title = (v.title as string | undefined) ?? "MCP App";
        const toolResult = v.tool_result;

        const params = new URLSearchParams({ server_id: mcpServerId, uri: resourceUri });
        const url = `${VITE_ANALYSIS_AGENT_API}/mcp-resources/fetch?${params}`;
        const { signal } = abortRef.current ?? {};

        apiClient<string>(url, { expectsText: true, signal })
          .then((html) => {
            setEntries((prev) => {
              const next = new Map(prev);
              next.set(toolCallId, { html, title, toolResult });
              return next;
            });
          })
          .catch(() => {
            setEntries((prev) => {
              const next = new Map(prev);
              next.set(toolCallId, { html: null, title, toolResult });
              return next;
            });
          });
      },
    });

    return () => {
      sub.unsubscribe();
      abortRef.current?.abort();
    };
  }, [agent]);

  return entries;
}
